import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Clock, Salad, HeartHandshake, Shapes, DoorOpen, ArrowRight, Sprout, Star, Languages, GraduationCap } from "lucide-react";
import { useLang } from "../lang/LanguageContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Reveal, MaskedLine } from "@/components/Reveal";
import WavyDivider from "@/components/WavyDivider";
import Marquee from "@/components/Marquee";
import GrowingVine from "@/components/GrowingVine";
import CTABand from "@/components/CTABand";
import { Bee, SunDoodle, Ladybug, FlowerDoodle, SproutDoodle, WavyUnderline } from "@/components/Doodles";

const HERO_IMG = "/images/hero-room.jpg";
const ASIA_IMG = "/images/asia.jpg";
const CRAYON_GARDEN = "/images/crayon-garden.jpg";
const LeafScene = () => (
    <svg viewBox="0 0 300 260" className="w-full max-w-sm mx-auto" role="img" aria-label="Illustration of a large leaf with a ladybug and a young sprout">
        <circle cx="150" cy="130" r="120" fill="#8FB38A" opacity="0.14" />
        <path d="M60 170C60 90 130 40 240 40c-4 110-70 160-150 150-18-2-30-10-30-20z" fill="#8FB38A" />
        <path d="M78 168C110 130 170 80 228 52" stroke="#5E8355" strokeWidth="4" strokeLinecap="round" fill="none" />
        <path d="M120 140c-2-18 2-32 12-44M160 112c-2-14 2-26 10-34" stroke="#5E8355" strokeWidth="3" strokeLinecap="round" fill="none" />
        <g transform="translate(196 96)">
            <circle r="13" fill="#E07A5F" stroke="#3D3229" strokeWidth="2.4" />
            <path d="M0 -13V13" stroke="#3D3229" strokeWidth="2" />
            <circle cx="-5" cy="-2" r="1.9" fill="#3D3229" />
            <circle cx="5" cy="-2" r="1.9" fill="#3D3229" />
            <circle cx="-4" cy="6" r="1.6" fill="#3D3229" />
            <path d="M-8 -11a6 6 0 0 1 12 0" fill="#3D3229" />
        </g>
        <g transform="translate(52 218)">
            <path d="M0 0V-26" stroke="#5E8355" strokeWidth="4" strokeLinecap="round" />
            <path d="M0 -14c-13 1.5-18-2.8-19.5-11 10-1.6 17.3 2.4 19.5 11z" fill="#F6C544" stroke="#3D3229" strokeWidth="2" />
            <path d="M0 -19c8.5-6.5 17-6.5 21.5-1.4-4.7 6-13 7-21.5 1.4z" fill="#F6C544" stroke="#3D3229" strokeWidth="2" />
            <ellipse cx="0" cy="4" rx="12" ry="4.5" fill="#E07A5F" />
        </g>
    </svg>
);

const WHY = [
    { icon: HeartHandshake, color: "bg-sage/20 text-sageink", title: "Small & Personal", text: "A warm, home-based setting where every child is known, valued, and cared for as an individual." },
    { icon: Shapes, color: "bg-sun/25 text-soil", title: "Learning Through Play", text: "Early literacy, numbers, art, music, movement, nature, and school-readiness skills are woven into meaningful, hands-on play." },
    { icon: Salad, color: "bg-coral/20 text-coraldeep", title: "Wholesome Meals Included", text: "Breakfast, lunch, and an afternoon snack are provided. Planned in accordance with USDA CACFP guidelines." },
    { icon: DoorOpen, color: "bg-skysoft text-sageink", title: "Open Door, Always", text: "You're always welcome here. Parents and guardians may visit anytime while your child is in our care. No appointment required. We believe trust grows through openness, and we love sharing your child's day with you." },
];

const STAGES = [
    {
        id: "seedlings",
        name: "Seedlings",
        group: "Infants",
        accent: "bg-skysoft border-sky",
        doodle: <SproutDoodle size={44} />,
        text: "Gentle routines, cozy naps, and plenty of holding, singing, and talking — our youngest seeds get exactly what they need to sprout.",
        ages: "6 weeks to 2 years",
        link: "/program",
        linkLabel: "See a day with us",
    },
    {
        id: "sprouts",
        name: "Sprouts",
        group: "Toddlers",
        accent: "bg-sun/15 border-sun",
        doodle: <SunDoodle size={44} />,
        text: "Busy hands, first words, big feelings. Toddlers dig, splash, dance, and explore in a safe space made just for them.",
        ages: "2 years",
        link: "/program",
        linkLabel: "See a day with us",
    },
    {
        id: "blooms",
        name: "Blooms",
        group: "Preschool",
        accent: "bg-coral/10 border-coral",
        doodle: <FlowerDoodle size={44} />,
        text: "Letters, numbers, friendships, and confidence grow every day through play — ready for kindergarten and beyond.",
        ages: "3 to 5 years",
        link: "/program",
        linkLabel: "See a day with us",
    },
    {
        id: "sunflowers",
        name: "Sunflowers",
        group: "School-Age · K–12",
        accent: "bg-sage/10 border-sage",
        doodle: <FlowerDoodle size={44} petals="#F6C544" />,
        text: "Before-school and after-school care, plus full days on school closures and breaks — a calm, cozy bookend to the school day.",
        ages: "Kindergarten to 12 years",
        link: "/enroll",
        linkLabel: "See school-age rates",
    },
];

const PHOTOS = [
    { src: "/images/play-1.jpg", alt: "Our bright play space with a rainbow rug, wooden shelves, and toys", caption: "Our play space" },
    { src: "/images/play-2.jpg", alt: "Play kitchen and dress-up corner with wooden furniture", caption: "Pretend play" },
    { src: "/images/play-3.jpg", alt: "Cozy reading corner with books, pillows, and a crib for naps", caption: "Cozy corner" },
    { src: "/images/play-4.jpg", alt: "Art supplies organized on shelves with colorful paper and bins", caption: "Art every day" },
    { src: "/images/play-5.jpg", alt: "Child-sized cubbies with orange bins next to shapes and colors charts", caption: "Learning centers" },
    { src: "/images/play-6.jpg", alt: "Kids' table with little blue chairs under numbers and alphabet charts", caption: "Snack & craft table" },
];

const SectionHead = ({ eyebrow, title, id }) => (
    <Reveal className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
        <p className="inline-flex items-center gap-2 bg-white/80 border-2 border-soil/10 rounded-full px-4 py-1.5 text-sm font-extrabold text-sageink mb-4">
            <FlowerDoodle size={15} /> {eyebrow}
        </p>
        <h2 id={id} className="font-display font-semibold text-3xl md:text-4xl lg:text-[2.6rem] leading-tight text-balance">
            {title}
        </h2>
    </Reveal>
);

export default function Home() {
    usePageTitle("Daycare in Massapequa Park, NY | Little Seeds Flourish");
    const { lang, t } = useLang();
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
    const decoY = useTransform(scrollYProgress, [0, 1], [0, -50]);
    const location = useLocation();

    useEffect(() => {
        if (location.hash !== "#en-espanol") return;
        const t = setTimeout(() => {
            const el = document.getElementById("en-espanol");
            if (!el) return;
            if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
            else el.scrollIntoView({ behavior: "smooth" });
        }, 80);
        return () => clearTimeout(t);
    }, [location]);

    return (
        <div>
            <GrowingVine />

            {/* 1. HERO */}
            <section ref={heroRef} className="relative overflow-x-clip" data-testid="home-hero">
                <div aria-hidden="true" className="absolute top-0 right-0 w-[320px] h-[320px] sm:w-[540px] sm:h-[540px] rounded-full bg-sage/12 blur-3xl" />
                <div aria-hidden="true" className="absolute top-64 -left-24 w-[240px] h-[240px] sm:top-64 sm:-left-32 sm:w-[420px] sm:h-[420px] rounded-full bg-skysoft blur-2xl opacity-60 sm:opacity-100" />
                <SunDoodle className="absolute top-24 right-6 lg:right-16 hidden md:block" size={72} />

                <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-10 md:pt-20 pb-16 md:pb-24 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-8 items-center">
                    <div>
                        <Reveal>
                            <p className="inline-flex items-center gap-2 bg-white/80 border-2 border-soil/10 rounded-full px-4 py-1.5 text-sm font-extrabold text-sageink mb-6">
                                <Sprout size={15} /> Massapequa Park, NY · Group Family Daycare
                            </p>
                        </Reveal>
                        <h1 className="font-display font-semibold text-[2.6rem] leading-[1.06] sm:text-5xl lg:text-6xl lg:leading-[1.05] text-soil">
                            <MaskedLine delay={0.1}>
                                {lang === "es" ? "Donde las pequeñas semillas" : "Where Little Seeds"}
                            </MaskedLine>
                            <MaskedLine delay={0.24}>
                                {lang === "es" ? (
                                    <span className="block mt-3 text-2xl sm:text-3xl lg:text-4xl font-medium text-soil/70 leading-snug">
                                        crecen para convertirse en <span className="text-sageink">niños seguros de sí mismos,</span> <span className="text-coral">creativos y valientes.</span>
                                    </span>
                                ) : (
                                    <span className="block mt-3 text-2xl sm:text-3xl lg:text-4xl font-medium text-soil/70 leading-snug">
                                        Develop into confident, creative and <span className="text-coral">Courageous</span> <span className="text-sageink">Kids.</span>
                                    </span>
                                )}
                            </MaskedLine>
                        </h1>
                        <Reveal delay={0.5}>
                            <p className="mt-5 text-lg text-soilmuted max-w-lg leading-relaxed">
                                {t("A play-based group family daycare in Massapequa Park, NY.")}
                            </p>
                        </Reveal>
                        <Reveal delay={0.58}>
                            <p className="mt-4 font-display font-semibold text-xl md:text-2xl" data-testid="hero-tagline">
                                {lang === "es" ? (
                                    <>
                                        <span className="text-sageink">Juega.</span> <span className="text-coral">Aprende.</span>{" "}
                                        <span className="text-sageink">Crece.</span> <span className="text-coral">Florece.</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="text-sageink">Play.</span> <span className="text-coral">Learn.</span>{" "}
                                        <span className="text-sageink">Grow.</span> <span className="text-coral">Flourish.</span>
                                    </>
                                )}
                            </p>
                        </Reveal>
                        <Reveal delay={0.62}>
                            <div className="mt-7 flex flex-wrap gap-3.5">
                                <Link
                                    to="/contact"
                                    data-testid="hero-schedule-tour-button"
                                    className="inline-flex items-center gap-2 bg-sun hover:bg-suncdeep text-soil font-extrabold text-lg px-8 py-4 rounded-full shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all border-2 border-soil/10"
                                >
                                    {t("Schedule a Tour")} <ArrowRight size={19} />
                                </Link>
                                <Link
                                    to="/program"
                                    data-testid="hero-explore-program-button"
                                    className="inline-flex items-center gap-2 border-2 border-sage text-sageink hover:bg-sage/10 font-extrabold text-lg px-8 py-4 rounded-full transition-all hover:-translate-y-0.5"
                                >
                                    {t("Explore Our Program")}
                                </Link>
                            </div>
                        </Reveal>
                        <Reveal delay={0.74}>
                            <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm font-bold text-soil/75" data-testid="hero-trust-line">
                                <li className="flex items-center gap-1.5"><ShieldCheck size={16} className="text-sageink" /> {t("OCFS license active")}</li>
                                <li className="flex items-center gap-1.5"><Clock size={16} className="text-sageink" /> {t("Open 6:00 AM – 8:00 PM")}</li>
                                <li className="flex items-center gap-1.5"><Salad size={16} className="text-sageink" /> {t("Meals Included")}</li>
                                <li className="flex items-center gap-1.5"><Languages size={16} className="text-sageink" /> Se habla español</li>
                                <li className="flex items-center gap-1.5"><GraduationCap size={16} className="text-sageink" /> {t("Bilingual instruction")}</li>
                            </ul>
                        </Reveal>
                    </div>

                    <div className="relative">
                        <motion.div style={{ y: imgY }} className="relative">
                            <img
                                src={HERO_IMG}
                                alt="The Little Seeds Flourish play space — learning charts, a cozy reading nook, a colorful play rug, and a crib, framed with a Little Seeds sprout and a bee"
                                className="w-full h-auto"
                                loading="eager"
                            />
                        </motion.div>
                    </div>
                </div>
                <div className="flex justify-center pb-6" aria-hidden="true">
                    <span className="flex items-center gap-2 text-xs font-extrabold tracking-widest uppercase text-soil/40">
                        {t("Scroll to grow")} <Sprout size={14} className="animate-bounce" />
                    </span>
                </div>
            </section>

            <Marquee />

            {/* 2. WHY FAMILIES CHOOSE US */}
            <section className="py-16 md:py-24" aria-labelledby="why-heading">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <SectionHead eyebrow={t("Why families choose us")} title={t("The warmth of home. The intention of a thoughtfully designed program.")} id="why-heading" />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {WHY.map((w, i) => (
                            <Reveal key={w.title} delay={i * 0.08} className="h-full">
                                <div className="hover-lift bg-creamsoft border-2 border-soil/10 rounded-3xl p-6 h-full" data-testid={`why-card-${i + 1}`}>
                                    <span className={`inline-flex p-3 rounded-2xl ${w.color} mb-4`}>
                                        <w.icon size={26} />
                                    </span>
                                    <h3 className="font-display font-semibold text-xl mb-2">{t(w.title)}</h3>
                                    <p className="text-soilmuted text-[15px] leading-relaxed">{t(w.text)}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* 3. MISSION */}
            <section className="py-16 md:py-24 bg-creamsoft relative overflow-x-clip" aria-labelledby="mission-heading">
                <div className="absolute inset-0" aria-hidden="true">
                    <img src={CRAYON_GARDEN} alt="" className="absolute right-0 bottom-0 w-56 opacity-[0.08] rotate-3" />
                </div>
                <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-10 items-center">
                    <Reveal>
                        <LeafScene />
                    </Reveal>
                    <div>
                        <Reveal>
                            <p className="inline-flex items-center gap-2 bg-white/80 border-2 border-soil/10 rounded-full px-4 py-1.5 text-sm font-extrabold text-sageink mb-4">
                                <FlowerDoodle size={15} /> {t("Our mission")}
                            </p>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <h2 id="mission-heading" className="font-display font-semibold text-3xl md:text-4xl leading-tight mb-5 text-balance">
                                {t("Our Mission")}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.16}>
                            <div className="space-y-4 text-lg text-soilmuted leading-relaxed" data-testid="home-mission-text">
                                <p>
                                    {t("At Little Seeds Flourish, our mission is to provide a safe, loving, inclusive, and enriching environment. Where every child feels valued, supported, and encouraged to flourish.")}
                                </p>
                                <p>
                                    {t("We believe early childhood is about more than preparing children academically. It is a phase for building confidence, curiosity, independence, kindness, resilience, and meaningful relationships.")}
                                </p>
                                <p>
                                    {t("Through purposeful play, exploration, creativity, age-appropriate learning, and nurturing interactions, we support the development of the child — socially, emotionally, cognitively, and physically.")}
                                </p>
                                <p className="font-bold text-sageink">
                                    {t("Our goal is simple: to give every little seed the strong roots, loving care, and meaningful experiences they need to play, learn, grow, and flourish.")}
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* 3.5 EN ESPAÑOL */}
            <section id="en-espanol" className="py-16 md:py-24 relative overflow-x-clip" aria-labelledby="espanol-heading" data-testid="home-espanol-section">
                <div aria-hidden="true" className="absolute top-8 left-1/2 -translate-x-1/2 w-[320px] h-[320px] sm:w-[560px] sm:h-[560px] rounded-full bg-sun/20 blur-3xl" />
                <Bee className="absolute top-14 right-6 lg:right-20 hidden md:block" size={54} />
                <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
                    <Reveal>
                        <p className="inline-flex items-center gap-2 bg-white/80 border-2 border-soil/10 rounded-full px-4 py-1.5 text-sm font-extrabold text-sageink mb-6">
                            <FlowerDoodle size={15} /> En Español
                        </p>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <h2 id="espanol-heading" className="font-display font-semibold text-3xl md:text-4xl lg:text-[2.6rem] leading-tight text-balance">
                            ¡Bienvenidos! <span className="text-coral">Se habla español.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <div className="mt-6 space-y-4 text-lg text-soilmuted leading-relaxed" data-testid="home-espanol-text">
                            <p>
                                Hola, me llamo Asia. En Little Seeds Flourish, cada niño es como una pequeña semilla: único,
                                especial y lleno de potencial. Ofrecemos un ambiente seguro, cálido y lleno de amor, donde los
                                niños juegan, aprenden, crecen y florecen a su propio ritmo.
                            </p>
                            <p>
                                Las familias que hablan español son muy bienvenidas. Con gusto contesto sus preguntas, les doy
                                un recorrido y les explico todos los formularios en español.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.24}>
                        <Link
                            to="/contact"
                            data-testid="espanol-schedule-button"
                            className="mt-8 inline-flex items-center gap-2 bg-coral hover:bg-coraldeep text-white font-extrabold text-lg px-8 py-4 rounded-full shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
                        >
                            Programar una visita <ArrowRight size={19} />
                        </Link>
                        <p className="mt-4 text-sm font-bold text-soil/60">
                            Llame o envíe un mensaje de texto al{" "}
                            <a href="tel:+15163243418" data-testid="espanol-phone-link" className="text-sageink underline underline-offset-4">
                                516-324-3418
                            </a>
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* 4. GROWTH STAGES */}
            <section className="py-16 md:py-24" aria-labelledby="stages-heading">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <SectionHead eyebrow={t("Growth stages")} title={t("Every child blooms in their own season")} id="stages-heading" />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {STAGES.map((s, i) => (
                            <Reveal key={s.id} delay={i * 0.1} className="h-full">
                                <div className={`hover-lift border-2 rounded-[2rem] p-7 h-full flex flex-col ${s.accent}`} data-testid={`growth-stage-card-${s.id}`}>
                                    <div className="flex items-center justify-between mb-4">
                                        {s.doodle}
                                        <span className="font-display font-semibold text-sm bg-white/80 rounded-full px-3.5 py-1.5">{t(s.group)}</span>
                                    </div>
                                    <h3 className="font-display font-semibold text-2xl mb-2">{s.name}</h3>
                                    <p className="text-soilmuted text-[15px] leading-relaxed flex-1">{t(s.text)}</p>
                                    <p className="mt-4 text-xs font-extrabold uppercase tracking-wider text-soil/45">{t(s.ages)}</p>
                                    <Link
                                        to={s.link}
                                        data-testid={`growth-stage-link-${s.id}`}
                                        className="mt-4 inline-flex items-center gap-1.5 font-extrabold text-sageink hover:gap-3 transition-all"
                                    >
                                        {t(s.linkLabel)} <ArrowRight size={16} />
                                    </Link>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <div className="bg-cream">
                <WavyDivider fill="#FFF3E2" />
            </div>

            {/* 5. PHOTO STRIP */}
            <section className="py-4 md:py-10 bg-creamsoft" aria-label="Photos of our space and activities">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <figure className="max-w-3xl mx-auto mb-10 md:mb-14" data-testid="home-play-collage">
                            <img
                                src="/images/play-collage.jpg"
                                alt="Little Seeds Flourish — children reading, crafting, building, and playing with learning cubes"
                                loading="lazy"
                                className="w-full rounded-[2rem] border-4 border-white shadow-lg"
                            />
                            <figcaption className="mt-3 text-center text-xs font-extrabold uppercase tracking-wider text-soil/50">
                                {t("Where little minds flourish through play")}
                            </figcaption>
                        </figure>
                    </Reveal>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5" data-testid="home-photo-strip">
                        {PHOTOS.map((p, i) => (
                            <Reveal key={p.src} delay={i * 0.06} className={i % 3 === 0 ? "md:mt-8" : ""}>
                                <figure className="group">
                                    <div className="overflow-hidden rounded-[1.8rem] border-4 border-white shadow-md aspect-square">
                                        <img src={p.src} alt={p.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    </div>
                                    <figcaption className="mt-2 text-xs font-extrabold uppercase tracking-wider text-soil/50 px-1">{t(p.caption)}</figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* 7. FOUNDER SECTION */}
            <section className="py-16 md:py-24 bg-creamsoft" aria-labelledby="founder-heading" data-testid="home-founder-section">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-[0.8fr_1.2fr] gap-10 md:gap-14 items-start">
                    <Reveal>
                        <div className="relative max-w-[300px] mx-auto md:mx-0 md:sticky md:top-28">
                            <div className="blob-frame-2 overflow-hidden border-[6px] border-white shadow-xl aspect-square">
                                <img src={ASIA_IMG} alt="Asia, founder of Little Seeds Flourish, smiling" loading="lazy" className="w-full h-full object-cover" />
                            </div>
                            <Ladybug className="absolute -top-4 -right-2" />
                        </div>
                    </Reveal>
                    <div>
                        <Reveal>
                            <p className="inline-flex items-center gap-2 bg-white/80 border-2 border-soil/10 rounded-full px-4 py-1.5 text-sm font-extrabold text-sageink mb-4">
                                <FlowerDoodle size={15} /> {t("Our founder")}
                            </p>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <h2 id="founder-heading" className="font-display font-semibold text-3xl md:text-4xl leading-tight mb-5 text-balance">
                                {t("Meet the Heart Behind Little Seeds Flourish")}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.16}>
                            <div className="space-y-4 text-lg text-soilmuted leading-relaxed">
                                <p>{t("Hi, I'm Asia — founder of Little Seeds Flourish and a mother of two.")}</p>
                                <p>
                                    {t("I created the place I would choose for my own children: warm and nurturing like home, with intentional opportunities to learn, explore, and build confidence — because")}{" "}
                                    <span className="font-bold text-sageink">{t("people flourish when they feel safe, valued, connected, and encouraged.")}</span>
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.24}>
                            <Link
                                to="/about"
                                data-testid="home-founder-story-button"
                                className="mt-7 inline-flex items-center gap-2 bg-coral hover:bg-coraldeep text-white font-extrabold text-lg px-8 py-4 rounded-full shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
                            >
                                {t("Read my story")} <ArrowRight size={19} />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            <CTABand />
        </div>
    );
}
